import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { LogoWordmark } from "../components/Logo";
import "./landing.css";

// The public front page at 30min.tn: the whole ecosystem (till, owner app, marketplace,
// drivers) and the plans, in the words of the printed brochure (sales/30min-depliant.html).
// Screenshots in public/landing/ are the brochure's own; app icons come from
// scripts/app-icons.mjs. Keep the prices here in step with the brochure.

type App = {
  id: string;
  name: string;
  icon: string;
  platforms: string[];
  title: [string, string];
  points: [string, string][];
  shots: { src: string; alt: string }[];
  /** Shown instead of screenshots, for an app with none: how it goes, step by step. */
  steps?: string[];
};

const APPS: App[] = [
  {
    id: "caisse",
    name: "30min Caisse",
    icon: "/app-icons/pos.svg",
    platforms: ["Tablette Android", "PC Windows"],
    title: ["Encaissez plus vite.", "Ne perdez plus un dinar."],
    points: [
      ["Plus rapide au rush", "Un toucher par article, la grille est rangée comme votre vitrine."],
      ["Fini les erreurs de prix", "Les prix sont dans la caisse, pas dans la tête du caissier."],
      ["Chaque caissier responsable", "Code PIN personnel\u00a0: vous savez qui a vendu quoi, et qui a un manque à la clôture."],
      ["Fini le cahier des crédits", "Les dettes clients sont notées, et réglées en un toucher."],
      ["Internet coupé\u00a0? Vous vendez quand même.", "La caisse marche 100\u00a0% hors-ligne."],
    ],
    shots: [{ src: "/landing/caisse-grille.webp", alt: "La grille de vente de 30min Caisse" }],
  },
  {
    id: "pro",
    name: "30min Pro",
    icon: "/app-icons/store.svg",
    platforms: ["Android"],
    title: ["Absent\u00a0?", "Tout reste sous contrôle."],
    points: [
      ["Vos ventes du jour, sur votre téléphone", "Où que vous soyez, par article et par caissier."],
      ["Repérez les manques de caisse", "Chaque clôture montre l'écart, caissier par caissier."],
      ["Payez votre équipe sans papier", "Salaires et avances, notés et suivis."],
      ["Dettes et stock toujours à jour", "Même quand vous n'êtes pas au magasin."],
      ["Chaque dépense justifiée", "Notée avec la photo du ticket."],
    ],
    shots: [
      { src: "/landing/pro-recette.webp", alt: "La recette du jour dans 30min Pro" },
      { src: "/landing/pro-commandes.webp", alt: "Les commandes en ligne dans 30min Pro" },
      { src: "/landing/pro-historique.webp", alt: "L'historique des ventes dans 30min Pro" },
    ],
  },
  {
    id: "cuisine",
    name: "30min Cuisine",
    icon: "/app-icons/pos-kitchen.svg",
    platforms: ["Tablette ou téléphone Android"],
    title: ["La cuisine suit la caisse,", "sans un papier."],
    points: [
      ["Chaque vente arrive en cuisine", "Encaissée au comptoir, la commande s'affiche aussitôt sur l'écran de la cuisine."],
      ["Un toucher : « Prêt »", "Le cuisinier marque la commande prête, son numéro passe sur l'écran TV de la salle (30min AD)."],
      ["Un toucher : « Servi »", "Récupérée par le client, elle quitte l'écran : plus d'appel au micro, plus d'oubli."],
      ["Seulement ce qui se prépare", "Les boissons et articles hors cuisine n'encombrent pas l'écran."],
    ],
    shots: [],
    steps: ["Vendu à la caisse", "En préparation en cuisine", "« Prêt » : le numéro s'affiche sur la TV", "« Servi » : le client l'a récupéré"],
  },
  {
    id: "client",
    name: "30min Client",
    icon: "/app-icons/customer.svg",
    platforms: ["Android", "iOS"],
    title: ["De nouveaux clients,", "qui reviennent."],
    points: [
      ["Visible dans toute la ville", "Votre commerce sur la marketplace 30min."],
      ["Ils commandent en quelques gestes", "Livraison ou à emporter, paiement en espèces."],
      ["Ils reviennent pour leurs points", "Des points de fidélité à chaque commande."],
      ["Votre réputation, prouvée", "Seuls les vrais clients livrés laissent un avis."],
      ["Ils commandent à l'avance", "Et récupèrent au comptoir avec leur code."],
    ],
    shots: [
      { src: "/landing/client-commerces.webp", alt: "Les commerces dans 30min Client" },
      { src: "/landing/client-retrait.webp", alt: "Le code de retrait dans 30min Client" },
    ],
  },
  {
    id: "driver",
    name: "30min Driver",
    icon: "/app-icons/driver.svg",
    platforms: ["Android"],
    title: ["Livré vite,", "par vos livreurs ou les nôtres."],
    points: [
      ["Des livreurs commandent et récupèrent chez vous", "En un éclair, sans chercher un livreur."],
      ["Votre flotte, sous contrôle", "Vous attribuez les courses, chacun les reçoit sur son téléphone."],
      ["Le cash suivi", "Vous confirmez ce que chaque livreur vous remet."],
      ["Le client est prévenu", "À chaque étape de sa commande."],
    ],
    shots: [],
    steps: ["Il accepte la course", "Il récupère chez vous, code vérifié", "Il livre votre client"],
  },
];

// Intrinsic sizes of the screenshots, so the page keeps its layout while they load.
const SHOT_SIZE: Record<string, { width: number; height: number }> = {
  "/landing/caisse-grille.webp": { width: 1000, height: 628 },
  "/landing/pro-recette.webp": { width: 420, height: 820 },
  "/landing/pro-commandes.webp": { width: 420, height: 817 },
  "/landing/pro-historique.webp": { width: 420, height: 820 },
  "/landing/client-commerces.webp": { width: 420, height: 817 },
  "/landing/client-retrait.webp": { width: 420, height: 817 },
};


// The offer, in three layers: the till bought once, the apps paid by the day on top of
// it, and online sales paid in packs of orders (or order by order).
type Till = { id: string; name: string; platform: string; icon: string; price: number };

const TILLS: Till[] = [
  { id: "android", name: "Caisse Android", platform: "Tablette Android", icon: "/app-icons/pos.svg", price: 600 },
  { id: "windows", name: "Caisse Windows", platform: "PC Windows", icon: "/app-icons/pos-windows.svg", price: 700 },
];

type Addon = {
  id: string;
  name: string;
  icon: string;
  /** Price in DT per day. */
  perDay: number;
  badge?: string;
  /** The app we push: dark card, raised, its own button colour. */
  featured?: boolean;
  tagline: string;
  points: string[];
  /** Other apps that come with this one at no extra cost. */
  includes?: { name: string; icon: string }[];
};

const ADDONS: Addon[] = [
  {
    id: "pro",
    name: "30min Pro",
    icon: "/app-icons/store.svg",
    perDay: 3.5,
    featured: true,
    tagline: "Gérez votre commerce depuis votre téléphone.",
    points: [
      "Ventes, clôtures et personnel sur le téléphone",
      "Salaires, avances et dépenses",
      "Dettes et stock, en ligne",
      "30min AD\u00a0: votre menu, vos offres et les commandes prêtes sur l'écran TV",
      "30min Cuisine\u00a0: la cuisine marque les commandes prêtes, puis récupérées",
    ],
    includes: [
      { name: "30min AD", icon: "/app-icons/tv.svg" },
      { name: "30min Cuisine", icon: "/app-icons/pos-kitchen.svg" },
    ],
  },
  {
    id: "driver",
    name: "30min Driver",
    icon: "/app-icons/driver.svg",
    perDay: 1.5,
    tagline: "Vos livreurs, sous contrôle.",
    points: ["Vous attribuez les courses, chacun les reçoit sur son téléphone", "Le cash de chaque livreur suivi"],
  },
];

// Online sales on the 30min Client marketplace: a pack of orders a month, paid by the day,
// or nothing up front and a fixed price per order.
const ORDER_PACKS = [
  { orders: 150, perDay: 1 },
  { orders: 250, perDay: 1.5 },
  { orders: 350, perDay: 2 },
];
const PER_ORDER = 0.22;

// A month of days, for the yearly price.
const MONTH_DAYS = 30;
// Paid by the year, 12 months cost 10: the backend's subscriptions.BilledMonths.
const YEAR_BILLED_MONTHS = 10;

function yearly(perDay: number) {
  return DT.format(perDay * MONTH_DAYS * YEAR_BILLED_MONTHS);
}

const DT = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 3 });
// To the millime (3 decimals), like a dinar price.
const MILLIMES = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });

// Built from the offer above, so the answers never drift from the prices.
const FAQ: [string, string][] = [
  [
    "La caisse 30min marche-t-elle sans internet\u00a0?",
    "Oui. 30min Caisse encaisse et imprime les tickets 100\u00a0% sans internet, même sans abonnement. " +
      "Les ventes remontent vers 30min Pro dès que la connexion revient.",
  ],
  [
    "Sur quel matériel installer la caisse\u00a0?",
    `Sur une tablette Android (${TILLS[0].price}\u00a0DT) ou un PC Windows (${TILLS[1].price}\u00a0DT), ` +
      "achetée une seule fois. On vient l'installer dans votre restaurant, snack ou café et on forme votre équipe.",
  ],
  [
    "Combien coûtent les applications de gestion\u00a0?",
    `${ADDONS.map((a) => `${a.name} coûte ${DT.format(a.perDay)}\u00a0DT par jour`).join(", ")}, ` +
      "sans engagement. Payées à l'année, 12\u00a0mois coûtent 10.",
  ],
  [
    "Comment vendre en ligne et livrer\u00a0?",
    "Votre menu apparaît sur la marketplace 30min.tn et dans l'app 30min Client. " +
      `Choisissez un pack de ${ORDER_PACKS[0].orders} à ${ORDER_PACKS[ORDER_PACKS.length - 1].orders} commandes par mois, ` +
      `ou payez ${MILLIMES.format(PER_ORDER)}\u00a0DT par commande, rien sans commande. ` +
      "Vos livreurs reçoivent les courses dans 30min Driver.",
  ],
];

const SITE = encodeURIComponent("https://30min.tn/");

// Addresses are put together on click, so none sits in the HTML for scrapers to harvest.
function MailLink({ to = "contact", subject, className, children }: {
  to?: string;
  subject?: string;
  className: string;
  children: ReactNode;
}) {
  return (
    <a
      className={className}
      href="#contact"
      onClick={(e) => {
        e.preventDefault();
        const query = subject ? `?subject=${encodeURIComponent(subject)}` : "";
        window.location.href = `mailto:${to}${String.fromCharCode(64)}30min.tn${query}`;
      }}
    >
      {children}
    </a>
  );
}

function TillCard({ till: t }: { till: Till }) {
  return (
    <article className="lp-plan lp-till">
      <div className="lp-plan-main">
        <div className="lp-plan-name">{t.name}</div>
        <div className="lp-price">
          <span className="lp-num">{t.price}</span>
          <span>DT, une seule fois</span>
        </div>
      </div>
      <div className="lp-plan-apps">
        <span>
          <img src={t.icon} alt={t.platform} width={28} height={28} />
          {t.platform}
        </span>
      </div>
      <ul className="lp-points lp-points-sm">
        <li><Check /><div>Ventes et tickets imprimés</div></li>
        <li><Check /><div>Marche 100&nbsp;% sans internet, même sans abonnement</div></li>
      </ul>
      <MailLink className="lp-plan-cta" subject={t.name}>
        Commander la caisse
      </MailLink>
    </article>
  );
}

function AddonCard({ addon: a }: { addon: Addon }) {
  const apps = [a, ...(a.includes ?? [])];
  return (
    <article className={`lp-plan lp-plan-${a.id}${a.featured ? " lp-featured" : ""}`}>
      <div className="lp-plan-main">
        <div className="lp-plan-name">
          {a.name.replace("30min ", "")}
          {a.badge && <span className="lp-badge">{a.badge}</span>}
        </div>
        <div className="lp-price">
          <em className="lp-plus">+</em>
          <span className="lp-num">{DT.format(a.perDay)}</span>
          <span>DT / jour</span>
        </div>
        <p className="lp-flex">Ou {yearly(a.perDay)}&nbsp;DT / an&nbsp;: 2&nbsp;mois offerts</p>
        <p className="lp-tagline">{a.tagline}</p>
      </div>
      <div className="lp-plan-extra">
        <div className="lp-plan-apps">
          {apps.map((x) => (
            <span key={x.name}>
              <img src={x.icon} alt={x.name} width={28} height={28} />
              {x.name}
            </span>
          ))}
        </div>
        <ul className="lp-points lp-points-sm">
          {a.points.map((t) => (
            <li key={t}>
              <Check />
              <div>{t}</div>
            </li>
          ))}
        </ul>
      </div>
      <MailLink className="lp-plan-cta" subject={`Offre ${a.name}`}>
        Ajouter {a.name.replace("30min ", "")}
      </MailLink>
    </article>
  );
}

function OnlineCards() {
  return (
    <>
      <div className="lp-online-head">
        <img src="/app-icons/customer.svg" alt="30min Client" width={44} height={44} />
        <p>
          <span className="lp-em">Vendez aussi sur la marketplace 30min.tn</span>, avec l'app 30min Client.
        </p>
      </div>
      <div className="lp-pack-grid">
        {ORDER_PACKS.map((k) => (
          <article key={k.orders} className="lp-plan lp-plan-online lp-pack">
            <div className="lp-plan-name">Pack {k.orders}</div>
            <p className="lp-pack-orders">
              <span className="lp-num">{k.orders}</span> commandes par mois
            </p>
            <div className="lp-price">
              <em className="lp-plus">+</em>
              <span className="lp-num">{DT.format(k.perDay)}</span>
              <span>DT / jour</span>
            </div>
            <p className="lp-flex">Ou {yearly(k.perDay)}&nbsp;DT / an</p>
            <MailLink className="lp-plan-cta" subject={`Offre Pack ${k.orders} commandes`}>
              Choisir ce pack
            </MailLink>
          </article>
        ))}
        <article className="lp-plan lp-plan-online lp-pack lp-pack-each">
          <div className="lp-plan-name">Sans pack</div>
          <p className="lp-pack-orders">Vous payez à la commande</p>
          <div className="lp-price">
            <span className="lp-num">{MILLIMES.format(PER_ORDER)}</span>
            <span>DT / commande</span>
          </div>
          <p className="lp-pack-note">Rien à payer sans commande</p>
          <MailLink className="lp-plan-cta" subject="Offre Vente en ligne à la commande">
            Choisir
          </MailLink>
        </article>
      </div>
    </>
  );
}

function Check() {
  return (
    <svg className="lp-check" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r="10" fill="currentColor" />
      <path d="M5.5 10.4l3 3 6-6.4" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function LandingPage() {
  return (
    <div className="lp">
      <header className="lp-top">
        <Link to="/" className="lp-logo" aria-label="30min.tn">
          <LogoWordmark />
        </Link>
        <nav className="lp-nav">
          <a href="#apps">Applications</a>
          <a href="/caisse/">La caisse</a>
          <a href="/tutoriels/">Tutoriels vidéo</a>
          <a href="#offres">Offres</a>
          <a href="#contact">Contact</a>
        </nav>
        <div className="lp-doors">
          <Link to="/staff/sign-in" className="lp-door">
            Espace staff
          </Link>
          <Link to="/restaurant/sign-in" className="lp-door lp-door-main">
            Espace business
          </Link>
        </div>
      </header>

      <main>
        <section className="lp-hero">
          <p className="lp-kicker">Caisse · Gestion · Vente en ligne · Livraison</p>
          <h1>
            La caisse et la livraison des restaurants en Tunisie,{" "}
            <em>dans une seule famille d'applications.</em>
          </h1>
          <p className="lp-lede">
            Une seule famille d'applications pour tout votre commerce&nbsp;: 30min encaisse au
            comptoir, vous montre vos comptes sur votre téléphone, vous amène de nouveaux clients
            et livre leurs commandes. Pensé pour les restaurants, snacks et cafés en Tunisie, même
            sans internet.
          </p>
          <div className="lp-hero-apps">
            {APPS.map((a) => (
              <a key={a.id} href={`#${a.id}`} className={`lp-chip lp-${a.id}`}>
                <img src={a.icon} alt={a.name} width={32} height={32} />
                {a.name}
              </a>
            ))}
          </div>
          <div className="lp-cta">
            <a className="lp-btn" href="#offres">
              Voir les offres
            </a>
            <a className="lp-btn lp-btn-ghost" href="/tutoriels/">
              Voir les tutoriels vidéo
            </a>
            <a className="lp-btn lp-btn-ghost" href="#contact">
              Nous contacter
            </a>
          </div>
        </section>

        <div id="apps">
          {APPS.map((a, i) => (
            <section key={a.id} id={a.id} className={`lp-app lp-${a.id}${i % 2 ? " lp-flip" : ""}`}>
              <div className="lp-app-text">
                <div className="lp-app-head">
                  <img src={a.icon} alt={a.name} width={56} height={56} />
                  <div>
                    <h2>{a.name}</h2>
                    <p className="lp-os">{a.platforms.join(" · ")}</p>
                  </div>
                </div>
                <h3>
                  {a.title[0]} <em>{a.title[1]}</em>
                </h3>
                <ul className="lp-points">
                  {a.points.map(([b, p]) => (
                    <li key={b}>
                      <Check />
                      <div>
                        <span className="lp-pt">{b}</span>
                        <p>{p}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`lp-shots lp-shots-${a.shots.length}`}>
                {a.shots.length === 0 ? (
                  <ol className="lp-steps">
                    {(a.steps ?? []).map((t, i) => (
                      <li key={t}>
                        <span>{i + 1}</span>
                        {t}
                      </li>
                    ))}
                  </ol>
                ) : (
                  a.shots.map((s) => (
                    <img key={s.src} src={s.src} alt={s.alt} loading="lazy" decoding="async" {...SHOT_SIZE[s.src]} />
                  ))
                )}
              </div>
            </section>
          ))}
        </div>

        <section id="offres" className="lp-plans">
          <h2>Nos offres</h2>
          <p className="lp-lede">
            La caisse, achetée une fois. Puis seulement les applications dont vous avez besoin,
            payées au jour.
          </p>
          <p className="lp-layer-label">
            <span>1</span>La caisse, achetée une fois
          </p>
          <div className="lp-till-grid">
            {TILLS.map((t) => (
              <TillCard key={t.id} till={t} />
            ))}
          </div>
          <p className="lp-layer-label">
            <span>2</span>Les applications, au choix
          </p>
          <div className="lp-plan-grid">
            {ADDONS.map((a) => (
              <AddonCard key={a.id} addon={a} />
            ))}
          </div>
          <p className="lp-layer-label">
            <span>3</span>La vente en ligne, par pack
          </p>
          <OnlineCards />
          <p className="lp-foot">
            Applications et packs sans engagement, en plus de la caisse. Payés à l'année, 12&nbsp;mois
            coûtent 10.
          </p>
        </section>

        <section id="questions" className="lp-faq">
          <h2>Questions fréquentes</h2>
          {FAQ.map(([q, a]) => (
            <div key={q} className="lp-faq-item">
              <h3>{q}</h3>
              <p>{a}</p>
            </div>
          ))}
        </section>

        <section id="contact" className="lp-contact">
          <h2>Parlons de votre commerce.</h2>
          <p>On vient l'installer chez vous et on forme votre équipe.</p>
          <div className="lp-cta">
            <MailLink className="lp-btn" to="info">
              Nous écrire
            </MailLink>
            <MailLink className="lp-btn lp-btn-ghost" subject="Demande d'offre">
              Demander une offre
            </MailLink>
          </div>
        </section>
      </main>

      <footer className="lp-legal">
        <span>© 30min.tn</span>
        <a href="/confidentialite">Politique de confidentialité</a>
        <Link to="/conditions">Conditions d’utilisation</Link>
        <span className="lp-share">
          Partager&nbsp;:
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${SITE}`} target="_blank" rel="noopener">Facebook</a>
          <a href={`https://wa.me/?text=${SITE}`} target="_blank" rel="noopener">WhatsApp</a>
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${SITE}`} target="_blank" rel="noopener">LinkedIn</a>
        </span>
      </footer>
    </div>
  );
}
